import { AutoAwesome } from '@mui/icons-material';
import { MenuItem, Radio, RadioGroup, Typography } from '@mui/material';
import { Alert, TextField, Tile, useIsMobile } from '@side/pantry';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useInteraction } from '@/common/hooks/useInteraction';
import { InteractionType } from '@/common/types/interaction';
import { isCommunityListingStatus } from '@/components/SideBar/useInteractionStatus/useInteractionStatus.helpers';
import {
  TrackInteractionsEvent,
  useTrackInteractionsAnalytics,
} from '$analytics/useTrackAnalytics/domains/interactions';
import { FormSection } from './FormSection';
import type { ErrorMessage, FormData } from './types';

const sideListingsAlert = (statusLabel: string) => (
  <Alert sx={{ mt: 1 }} icon={<AutoAwesome />} color="secondary">
    {`You'll have the option to share ${statusLabel} listings on the Listing Feed, an exclusive space for the community`}
  </Alert>
);

function StatusExplainer({ status, state }: { status: string; state: string }) {
  const showSideListingsAlert =
    !!state && isCommunityListingStatus({ status, market: state });

  return (
    {
      offMarket: (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            A property that is for sale but will not be shared on a public
            listing service
          </Typography>
          {showSideListingsAlert && sideListingsAlert('Off Market')}
        </>
      ),
      comingSoon: (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            A property that isn&apos;t officially on the market yet, but will be
            active within the allowed time frame according to your MLS
          </Typography>
          {showSideListingsAlert && sideListingsAlert('Coming Soon')}
        </>
      ),
      preListing: (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          When an agent has a prospective client and are in the interview or
          negotiation process, prior to an executed representation agreement
        </Typography>
      ),
      active: (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            A property that has been listed on the MLS
          </Typography>
          {showSideListingsAlert && sideListingsAlert('Active')}
        </>
      ),
      activeCont: (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            A property that has been accepted by a buyer but has contingencies
            that still need to be met before the transaction is closed
          </Typography>
          {showSideListingsAlert && sideListingsAlert('Active Contingent')}
        </>
      ),
    }[status] ?? null
  );
}

const statusOptions: {
  value: NonNullable<FormData['status']>;
  label: string;
}[] = [
  {
    value: 'offMarket',
    label: 'Off Market',
  },
  { value: 'comingSoon', label: 'Coming Soon' },
  { value: 'preListing', label: 'Pre-Listing' },
  { value: 'active', label: 'Active' },
  { value: 'activeCont', label: 'Active Contingent' },
];

interface Props {
  type: InteractionType;
  setErrorMessage: (error: ErrorMessage) => void;
  sourceInteractionId?: string;
}

export function StatusSelect({
  type,
  setErrorMessage,
  sourceInteractionId,
}: Props) {
  const { control, watch, setValue, getValues } = useFormContext<FormData>();
  const status = watch('status') ?? '';
  const state = watch('state') ?? '';

  // Pre-fill the status from the source interaction when cloning
  const { data: sourceInteraction, error: sourceError } = useInteraction(
    sourceInteractionId,
    type,
    !sourceInteractionId,
  );

  useEffect(() => {
    if (!sourceInteractionId) return;

    if (sourceError) {
      // Show the same error we show when we fail to get the property address
      setErrorMessage('errorCloneTransaction');
      return;
    }

    // Never clobber a status the user has already picked (including one chosen
    // while the source interaction was still loading)
    if (getValues('status')) return;

    // Only set the value when a visible button exists for the source status
    const supportedStatus = statusOptions.find(
      ({ value }) => value === sourceInteraction?.status,
    );
    if (supportedStatus) {
      setValue('status', supportedStatus.value, { shouldValidate: true });
    }
  }, [
    sourceInteractionId,
    sourceInteraction?.status,
    sourceError,
    setValue,
    getValues,
    setErrorMessage,
  ]);

  const isMobile = useIsMobile('md');
  const trackEvent = useTrackInteractionsAnalytics();

  if (type !== InteractionType.listing) return null;

  return (
    <FormSection sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        What&apos;s the current status of the listing? *
      </Typography>
      <Controller
        name="status"
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange } }) =>
          isMobile ? (
            <TextField
              select
              fullWidth
              value={status}
              onChange={(e) => {
                trackEvent(TrackInteractionsEvent.ClickStatusToggleButton, {
                  statusValue: e.target.value,
                });
                onChange(e.target.value);
              }}
            >
              {statusOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <RadioGroup
              value={status}
              onChange={(_e, v) => {
                onChange(v);
                trackEvent(TrackInteractionsEvent.ClickStatusToggleButton, {
                  statusValue: v,
                });
              }}
              row
              sx={{ gap: 1.25 }}
            >
              {statusOptions.map(({ value, label }) => (
                <Tile
                  key={value}
                  value={value}
                  label={label}
                  hideIcon
                  control={<Radio />}
                  sx={{ flexGrow: 0, gap: 0, margin: 0, textWrap: 'nowrap' }}
                />
              ))}
            </RadioGroup>
          )
        }
      />
      <StatusExplainer status={status} state={state} />
    </FormSection>
  );
}
